import React, { useEffect, useRef, useState } from 'react'
import { SearchPanel, SearchPanelProps } from '../SearchPanel/SearchPanel'
import { MaterialIconId } from '../MaterialIcon/MaterialIconId.type'
import { MaterialIcon } from '../MaterialIcon/MaterialIcon'
import { MaterialIconSize } from '../MaterialIcon/MaterialIconSize.type'
import { MenuItem } from '../../types/MenuItem.type'
import xss from 'xss'
import './style.module.scss'
import { BrandIcon } from '../BrandIcon/BrandIcon'
import { BrandIconId } from '../BrandIcon/BrandIconId.type'
import { LinkSize } from '../Link/LinkSize.type'
import { ConditionalLink, ConditionalLinkProps } from '../Link/ConditionalLink'
import { PersonalizedProspectDropdown } from '../AppBar/PersonalizedProspectDropdown'
import { makeUrlRelative } from '../../lib/CfgApi/makeUrlRelative'
import { useAppContext } from '../../context/useAppContext'
import { LinkVariant, UserStatus } from '../../main'
import { isAncestorPath } from '../../lib/helpers/isAncestorPath'
import { setLocalUserStatus } from '../../lib/helpers/userStatusStorage'

export type AppMenuProps = {
  className?: string
  menuItems: MenuItem[]
  searchPanel?: SearchPanelProps
  stickyLinks?: Omit<ConditionalLinkProps, 'userStatus'>[]
  testId?: string
}

export const AppMenu = (props: AppMenuProps) => {
  const { className, menuItems, searchPanel, stickyLinks, testId } = props
  const [navigationTree, setNavigationTree] = useState<number[]>([])
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [isSticky, setIsSticky] = useState(false)
  const [lastScrollY, setLastScrollY] = useState(0)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const { state: appState } = useAppContext()

  const handleScroll = () => {
    const currentScrollY = window.scrollY
    const rect = menuRef.current?.getBoundingClientRect()
    if (!rect) {
      return
    }

    if (window.scrollY > rect?.bottom) {
      setIsSticky(true)
    } else {
      setIsSticky(false)
    }

    // shy behavior
    if (currentScrollY > lastScrollY) {
      setIsSticky(false)
    }

    setIsSearchOpen(false)
    setLastScrollY(currentScrollY)
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  })

  useEffect(() => {
    if (isSearchOpen) {
      // TODO forward ref to avoid querying DOM
      const searchInput = document.querySelector('input[name="sp_q"]')
      if (searchInput instanceof HTMLInputElement) {
        searchInput.focus()
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsSearchOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isSearchOpen])

  function getSubMenuItems(
    arr: MenuItem[],
    indexes: number[]
  ): MenuItem[] | undefined {
    let value = arr
    for (const index of indexes) {
      if (value[index].menuItems === undefined) {
        return undefined
      }
      value = value[index].menuItems!
    }
    return value
  }

  const handleMenuKeyDown = (
    event: React.KeyboardEvent<HTMLAnchorElement>,
    indexes: number[]
  ) => {
    if (event.key === 'Enter') {
      setNavigationTree(indexes)
      const subMenuOptions = getSubMenuItems(menuItems, indexes)
      if (subMenuOptions?.length !== 0) {
        event.preventDefault()
      }
    }
  }

  const handleMenuMouseEnter = (indexes: number[]) => {
    setNavigationTree(indexes)
  }

  const handleMenuItemMouseOver = () => {
    setIsSearchOpen(false)
  }

  const handleSearchClick = () => {
    setNavigationTree([])
    setIsSearchOpen(prev => !prev)
  }

  const handleSearchClose = () => {
    setIsSearchOpen(false)
  }

  const handleBlur = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    timerRef.current = setTimeout(() => {
      setNavigationTree([])
    }, 1000)
  }

  const handleFocus = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
  }

  if (appState.isMenuLoading) {
    return <div className={`AppMenu u-bgColorLight ${className}`}></div>
  }

  if (menuItems.length === 0) {
    return null
  }

  // desktop version
  return (
    <div
      className={`AppMenu ${
        isSticky ? 'AppMenu--sticky' : 'AppMenu--default'
      } u-bgColorLight ${className}`}
      data-testid={testId}
    >
      <nav
        className="u-sizeConstrained"
        ref={menuRef}
        onMouseEnter={handleFocus}
        onMouseLeave={handleBlur}
      >
        <ul className="AppMenu-tier1 AppMenu-menuItems List List--selectable List--inline u-flexJustifyStart u-borderBottom0 u-padding0 u-margin0">
          {menuItems?.map((tier1Item, tier1Index) => (
            <li
              key={tier1Item.name}
              className={[
                'AppMenu-tier1MenuItem',
                'AppMenu-link',
                'u-padding0',
                navigationTree[0] === tier1Index ? 'is-open' : '',
                isAncestorPath(tier1Item.url) ? 'is-active' : ''
              ]
                .filter(Boolean)
                .join(' ')}
              onFocus={() =>
                tier1Index !== navigationTree[0] && setNavigationTree([])
              }
              onMouseOver={() => handleMenuItemMouseOver()}
            >
              <a
                href={xss(tier1Item.url)}
                className="u-flex u-paddingHoriz0 u-paddingVert3gu u-marginHoriz4gu u-textSizeMsr u-textNoWrap"
                onKeyDown={e => handleMenuKeyDown(e, [tier1Index])}
                onMouseEnter={() => handleMenuMouseEnter([tier1Index])}
              >
                {isSticky && tier1Item.name === 'Home' ? (
                  <BrandIcon
                    className="AppMenu-ampfCompass"
                    icon={BrandIconId.AMPF_COMPASS}
                    spriteUrl={''}
                  />
                ) : (
                  <span
                    dangerouslySetInnerHTML={{ __html: xss(tier1Item.name) }}
                  />
                )}
              </a>
              {tier1Item.menuItems && (
                <div className="AppMenu-tier2Wrapper">
                  <ul className="AppMenu-tier2">
                    {tier1Item.menuItems?.map((tier2Item, tier2Index) => (
                      <li
                        key={tier2Item.name}
                        className={`${
                          navigationTree[0] === tier1Index &&
                          navigationTree[1] === tier2Index
                            ? 'is-open'
                            : ''
                        }`}
                      >
                        <a
                          href={xss(tier2Item.url)}
                          onKeyDown={e =>
                            handleMenuKeyDown(e, [tier1Index, tier2Index])
                          }
                          onMouseEnter={() =>
                            handleMenuMouseEnter([tier1Index, tier2Index])
                          }
                        >
                          <span
                            dangerouslySetInnerHTML={{
                              __html: xss(tier2Item.name)
                            }}
                          />
                          {tier2Item.menuItems &&
                            tier2Item.menuItems?.length > 0 && (
                              <MaterialIcon
                                icon={MaterialIconId.CHEVRON_RIGHT}
                                size={MaterialIconSize.SIZE_18}
                                className="u-floatRight u-marginRight3gu"
                              />
                            )}
                        </a>
                        {tier2Item.menuItems &&
                          tier2Item.menuItems?.length > 0 && (
                            <div
                              className={`AppMenu-tier3Wrapper ${
                                tier1Item.menuItems &&
                                tier1Item.menuItems.length <
                                  tier2Index + tier2Item.menuItems.length &&
                                'AppMenu-tier3--overhang'
                              }`}
                            >
                              <ul className="AppMenu-tier3">
                                {tier2Item.menuItems?.map(
                                  (tier3Item, tier3Index) => (
                                    <li key={tier3Item.name}>
                                      <a
                                        href={xss(tier3Item.url)}
                                        onKeyDown={e =>
                                          handleMenuKeyDown(e, [
                                            tier1Index,
                                            tier2Index,
                                            tier3Index
                                          ])
                                        }
                                        onMouseEnter={() =>
                                          handleMenuMouseEnter([
                                            tier1Index,
                                            tier2Index,
                                            tier3Index
                                          ])
                                        }
                                      >
                                        <span
                                          dangerouslySetInnerHTML={{
                                            __html: xss(tier3Item.name)
                                          }}
                                        />
                                      </a>
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </li>
          ))}
          <li className="u-padding0 u-flexExpandLeft">
            <ul className="u-flex u-flexAlignItemsCenter u-flexJustifyBetween">
              {searchPanel && (
                <li className="AppMenu-link u-padding0">
                  <button
                    onClick={handleSearchClick}
                    className={`Link u-flex u-flexJustifyBetween u-paddingHoriz0 u-paddingVert3gu u-marginLeft4gu u-textSizeMsr u-textNoWrap ${
                      isSearchOpen ? 'is-open' : ''
                    }`}
                  >
                    {isSticky ? (
                      <MaterialIcon
                        icon={MaterialIconId.SEARCH}
                        size={MaterialIconSize.SIZE_16}
                      />
                    ) : (
                      'Search'
                    )}
                  </button>
                </li>
              )}
              {isSticky &&
                stickyLinks?.map(
                  (
                    link: Omit<ConditionalLinkProps, 'userStatus'>,
                    index: number
                  ) => (
                    <li
                      key={index}
                      className="AppMenu-link AppMenu-linkTopRight u-padding0 u-margin0"
                    >
                      {appState.ampUserStatus === UserStatus.PROSPECT &&
                      link.variant === LinkVariant.PRIMARY &&
                      link.name === 'Find an Advisor' &&
                      appState.advisorData?.name &&
                      (appState.advisorData?.url ||
                        appState.advisorData?.email ||
                        appState.advisorData?.phonenumber) ? (
                        <PersonalizedProspectDropdown
                          advisorData={appState.advisorData}
                          className="u-marginLeft4gu u-sm-hidden u-md-hidden"
                          testId={
                            testId
                              ? `${testId}-personalized-prospect-dropdown`
                              : undefined
                          }
                        />
                      ) : (
                        <ConditionalLink
                          {...link}
                          userStatus={appState.ampUserStatus!}
                          className={`u-marginLeft4gu ${link.className || ''}`}
                          href={makeUrlRelative(link.url)}
                          analyticsName={link.analyticsName}
                          size={LinkSize.SMALL}
                          testId={
                            testId ? `${testId}-link-${index}` : undefined
                          }
                          onClick={() =>
                            link.name === 'Log Out'
                              ? setLocalUserStatus(UserStatus.CLIENT_EXPIRED)
                              : undefined
                          }
                        />
                      )}
                    </li>
                  )
                )}
            </ul>
          </li>
        </ul>
      </nav>
      {searchPanel && (
        <SearchPanel
          open={isSearchOpen}
          searchBox={{ ...searchPanel.searchBox }}
          mostVisitedLinks={searchPanel.mostVisitedLinks}
          handleSearchClose={handleSearchClose}
        />
      )}
    </div>
  )
}
